import type {
  GetDynamicTokensConfig,
  DynamicTokenEntryPointMap,
  DynamicTokenEntryPoint,
} from '../DynamicTokenConfig.types';
import { DYNAMIC_TOKEN_ENTRY_POINT_MAP } from '../DynamicTokenConfig.types';
import { getDynamicBackgroundTokens } from './getDynamicBackgroundTokens';
import { getDynamicButtonTokens } from './getDynamicButtonTokens';
import { getDynamicNavItemTokens } from './getDynamicNavItemTokens';
import { getDynamicTextTokens } from './getDynamicTextTokens';

// Mark a single property as NonNullable.
type NonNullableProperty<T, K extends keyof T> = T &
  { [P in K]: NonNullable<T[P]> };

export type GetDynamicTokens<T extends DynamicTokenEntryPoint> = (
  config: NonNullableProperty<GetDynamicTokensConfig, 'background'>,
) => { [K in DynamicTokenEntryPointMap[T][number]]: string };

const DYNAMIC_TOKENS_GETTERS: {
  [K in DynamicTokenEntryPoint]: GetDynamicTokens<K>;
} = {
  'dynamic.background': getDynamicBackgroundTokens,
  'dynamic.button': getDynamicButtonTokens,
  'dynamic.navitem': getDynamicNavItemTokens,
  'dynamic.text': getDynamicTextTokens,
} as const;

/**
 * When no background is given, instead return an empty string for each token,
 * which will prompt `setDynamicTokens` to instead remove the property.
 * This is useful for things that are conditionally dynamic. For example,
 * a card back can have a card cover, in which case we want to color various
 * elements across the whole element dynamically. When a card cover doesn't
 * exist, we can instead use fallback values for static styling.
 */
const getEmptyTokens = <T extends DynamicTokenEntryPoint>(
  token: T,
): { [K in DynamicTokenEntryPointMap[T][number]]: '' } => {
  const result = {} as { [K in DynamicTokenEntryPointMap[T][number]]: '' };
  for (const name of DYNAMIC_TOKEN_ENTRY_POINT_MAP[token]) {
    result[name as DynamicTokenEntryPointMap[T][number]] = '';
  }
  return result;
};

/**
 * Given an entry point, returns associated dynamic token values. For example,
 * the 'dynamic.button' entry point will return 'dynamic.button',
 * 'dynamic.button.hovered', and 'dynamic.button.pressed'.
 *
 * The list of entry points can be found in `getDynamicTokens.ts`.
 */
export const getDynamicTokens = (
  token: DynamicTokenEntryPoint,
  { background, ...config }: GetDynamicTokensConfig,
) => {
  // Extra protection for non-TS callers.
  if (!Object.prototype.hasOwnProperty.call(DYNAMIC_TOKENS_GETTERS, token)) {
    throw new Error(`Invalid entry point for getDynamicTokens: ${token}.`);
  }
  if (!background) {
    return getEmptyTokens(token);
  }
  return DYNAMIC_TOKENS_GETTERS[token]({ ...config, background });
};
